import * as React from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Autocomplete, { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import { Country } from '@esg/esg-global-types';
import {
  country_label,
  generateFlagURI,
  getCountries
} from '../../../../lib/metric_capture/country';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { Checkbox } from '@mui/material';

interface CountrySelectProps {
  handleChangeCountries?: (countries: Array<Country> | Country | null) => void;
  defaultCountryToParent?: (countries: Array<Country>) => void;
  options?: Array<Country>;
  selected_options?: Array<Country> | Country | null;
  disabled?: boolean;
  input_label?: string;
  multi_select?: boolean;
  tag_limit?: number;
  hide_icons?: boolean;
  clearable?: boolean;
  group_id?: string;
  company_id?: string;
  master_list?: boolean;
  loading?: boolean;
  variant?: 'standard' | 'outlined';
  select_all?: boolean;
  disable_selected_tags?: boolean;
}

// Checkbox icons
const check_icon_blank = <CheckBoxOutlineBlankIcon fontSize="small" />;
const check_icon_filled = <CheckBoxIcon fontSize="small" />;

/**
 * Country selection auto-complete component that handles a variety of built-in scenarios.
 * @param {Array<Country> | Country | null} props.handleChangeCountries Callback function to notify the parent component when the selected options have changed.
 * @param {Array<Country>} props.defaultCountryToParent Callback function to set the default country value in a parent component from the list of country options
 * @param {Array<Country>} props.options Custom list of options provided by the parent component. Defaults to none.
 * @param {Array<Country> | Country | null} props.selected_options Custom list of selected options provided by the parent component. Changes component from an uncontrolled to controlled.
 * @param {boolean} props.disabled Renders the component in a disabled state along with any other provided components. Defaults to enabled.
 * @param {string} props.input_label Sets a custom component input label. Defaults to the singular version of the main entity name.
 * @param {string} props.group_by Group the options by any one of a variety of grouping options. Defaults to ungrouped.
 * @param {boolean} props.multi_select Allow multi option selection and keeps the select open while selecting. Defaults to a single select and closes after selecting an option.
 * @param {number} props.tag_limit Sets a limit on the amount of tags rendered in the input. Defaults to unlimited.
 * @param {boolean} props.hide_icons Optionally hide any start or end adornment icons from the autocomplete.
 * @param {boolean} props.clearable Allows the autocomplete to be cleared. Defaults to true.
 * @param {string} props.company_id Query configured countries for the provided company. Works in conjunction with props.group_id. Optionally works in conjunction with unconfigured_only or standard_ids. Defaults to empty list.
 * @param {boolean} props.master_list Forcefully query countries from the master list. Does not require group_id and company_id. Defaults to false.
 * @param {boolean} props.loading A flag to set the autocomplete into a loading state for things such as fetching entries from database.
 * @param {string} props.variant An optional styling parameter for the textbox input of the autocomplete.
 * @param {boolean} props.select_all Optionally add a select all checkbox to the input component of the autocomplete when multiple selection is allowed.
 * @param {boolean} props.disable_selected_tags Optionally replace the selected tags with text when autocomplete is expanded to render input at a predictable height.
 * @returns {JSX.Element}
 */
const CountrySelect = (props: CountrySelectProps) => {
  const [optionList, setOptions] = React.useState<Array<Country>>([]);
  const [selectedOptions, setSelectedOptions] = React.useState<Array<Country> | Country | null>(
    props.multi_select ? [] : null
  );

  // Check if all options for the autocomplete are currently selected.
  const checkAll = (): boolean => {
    if (Array.isArray(selectedOptions)) {
      return optionList.length === selectedOptions.length;
    }
    return false;
  };

  // Toggle a full selection / deselection of the options for the autocomplete.
  const changeAll = (): void => {
    if (Array.isArray(selectedOptions)) {
      if (selectedOptions.length > 0) {
        setSelectedOptions([]);
        props.handleChangeCountries && props.handleChangeCountries([]);
      } else {
        setSelectedOptions(optionList);
        props.handleChangeCountries && props.handleChangeCountries(optionList);
      }
    }
  };

  // Define autocomplete options
  React.useEffect(() => {
    (async () => {
      const options: Array<Country> = props.options
        ? // Countries supplied by the parent component.
          props.options
        : // All master list countries.
          props.master_list
          ? await getCountries(true)
          : props.group_id && props.company_id
            ? // Configured countries per company.
              await getCountries(false, props.group_id, props.company_id)
            : [];
      if (options) setOptions(options);
    })();
  }, [props.group_id, props.company_id, props.options]);

  // Define selected options
  React.useEffect(() => {
    const resolveFromOptionList = (country: Country | null | undefined): Country | null => {
      if (!country) return null;
      return optionList.find((option) => option.id === country.id) ?? country;
    };
    setSelectedOptions(
      props.multi_select
        ? Array.isArray(props.selected_options)
          ? props.selected_options
          : []
        : resolveFromOptionList(props.selected_options as Country | null)
    );
  }, [props.options, props.selected_options, optionList]);

  React.useEffect(() => {
    (async () => {
      if (props.defaultCountryToParent) {
        const countries: Array<Country> = props.options ? props.options : await getCountries(true);
        if (countries) {
          props.defaultCountryToParent(countries);
        }
      }
    })();
  }, []);

  return (
    <Autocomplete
      id="country-select"
      fullWidth
      disabled={props.disabled}
      loading={props.loading}
      limitTags={props.tag_limit}
      disableClearable={props.clearable === false}
      options={optionList}
      value={selectedOptions}
      getOptionLabel={(option: Country) => {
        return option.name;
      }}
      disableCloseOnSelect={props.multi_select}
      isOptionEqualToValue={(option: Country, value: Country) => option.id === value.id}
      multiple={props.multi_select}
      noOptionsText={`No ${country_label.many.toLowerCase()}`}
      renderOption={(render_props, option: Country, { selected }) => (
        <Box
          component="li"
          sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
          {...render_props}
          key={option.id}
        >
          {props.multi_select && (
            <Checkbox
              icon={check_icon_blank}
              checkedIcon={check_icon_filled}
              style={{ marginRight: 8 }}
              checked={selected}
            />
          )}
          {props.hide_icons !== true && (
            <img
              loading="lazy"
              width="20"
              srcSet={`${generateFlagURI(option.code)} 2x`}
              src={generateFlagURI(option.code)}
              alt={option.name}
            />
          )}
          {option.name}
        </Box>
      )}
      renderValue={
        !props.multi_select || !props.disable_selected_tags
          ? undefined
          : (value: Country | Array<Country>) => {
              return Array.isArray(value) && value.length > 0
                ? `${value.length} ${value.length === 1 ? country_label.one : country_label.many}`
                : undefined;
            }
      }
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          {...params}
          label={props.input_label ?? country_label.one}
          slotProps={{
            htmlInput: { ...params.inputProps },
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {props.select_all && props.multi_select && (
                    <Box component="div">
                      <Box
                        component="li"
                        style={{
                          fontSize: '0.85rem',
                          color: '#616161',
                          fontWeight: 500
                        }}
                      >
                        <Checkbox
                          icon={check_icon_blank}
                          checkedIcon={check_icon_filled}
                          checked={checkAll()}
                          onChange={() => {
                            changeAll();
                          }}
                        />
                        Select All
                      </Box>
                    </Box>
                  )}
                  {params.InputProps.endAdornment}
                </>
              )
            }
          }}
          InputProps={{
            ...params.InputProps,
            ...(!props.multi_select &&
              props.selected_options &&
              !Array.isArray(props.selected_options) &&
              props.selected_options.code &&
              props.hide_icons !== true && {
                startAdornment: (
                  <img
                    loading="lazy"
                    width="20"
                    style={{ marginRight: 12 }}
                    srcSet={`${generateFlagURI(props.selected_options.code)} 2x`}
                    src={generateFlagURI(props.selected_options.code)}
                    alt={props.selected_options.name}
                  />
                )
              })
          }}
          variant={props.variant ?? 'outlined'}
        />
      )}
      onChange={(
        event: React.SyntheticEvent<Element, Event>,
        value: Array<Country> | Country | null
      ) => {
        setSelectedOptions(value);
        props.handleChangeCountries && props.handleChangeCountries(value);
      }}
    />
  );
};

export default CountrySelect;
